import { computed, ref } from 'vue';
import { useCustomToast } from '@/shared/lib';
import { type BranchMember } from '@/entities/Branch';
import { usePositionUserSelection } from '@/entities/User';
import { fetchAllActiveMembers } from '../lib/fetchAllActiveMembers';

/**
 * Cargo/colaborador selection shared by every training form that lets the user pick
 * participants by position (SGQ Pontual, Treinamento Aberto, Homologação editing).
 */
export function useParticipantSelection() {
  const toast = useCustomToast();

  const members = ref<BranchMember[]>([]);
  const loading = ref(false);
  const loaded = ref(false);

  /**
   * includeInactiveMembers preserves an already-selected member across a
   * position-checkbox toggle even if they've since gone inactive (same
   * reasoning as useEditTrainingContent.ts) — it never makes a NEW inactive
   * member pickable, since mergeRecordedInactiveSelections only re-offers
   * members already present in selectedUsers. Needed for editing: without
   * it, touching any position checkbox on a training with an inactive
   * participant silently drops them from the saved selection.
   */
  const collaborators = usePositionUserSelection({
    mode: 'by-selected-positions',
    includeInactiveMembers: true,
  });

  /**
   * Colaboradores salvos que a lista por cargo não consegue exibir porque não
   * batem com nenhum dos cargos atualmente selecionados (mesmo truque de
   * useEditTrainingContent.ts): sem isto, tocar em qualquer cargo os podaria
   * da seleção (usePositionUserSelection reconcilia selectedUsers contra os
   * elegíveis) e o submit revogaria a participação deles em silêncio.
   */
  const unlistedMemberIds = ref<string[]>([]);

  const visibleMemberIds = computed(() => [
    ...new Set([...collaborators.selectedUsers.value, ...unlistedMemberIds.value]),
  ]);

  /**
   * Pré-seleciona, ao editar, os cargos e colaboradores salvos no próprio
   * treinamento (`training.positions`/`trainees` — não há positionId por
   * participante nesses tipos, a lista de cargos é do treinamento como um
   * todo). Colaboradores que não batem com nenhum desses cargos (mudaram de
   * cargo depois, por exemplo) vão para unlistedMemberIds.
   */
  async function initializeParticipants(positionIds: number[], memberIds: number[]): Promise<void> {
    const positionIdStrings = [...new Set(positionIds)].map(String);
    const memberIdStrings = [...new Set(memberIds)].map(String);

    if (positionIdStrings.length === 0) {
      unlistedMemberIds.value = memberIdStrings;
      return;
    }

    await collaborators.initializeSelections(positionIdStrings, memberIdStrings);

    const eligibleMemberIds = new Set(
      (collaborators.users.value.data ?? []).map((user) => String(user.memberId ?? ''))
    );
    unlistedMemberIds.value = memberIdStrings.filter((id) => !eligibleMemberIds.has(id));
  }

  async function load(): Promise<void> {
    if (loaded.value) return;

    loading.value = true;
    try {
      const [allMembers] = await Promise.all([fetchAllActiveMembers(), collaborators.fetchPositionsAndUsers()]);
      members.value = allMembers;
      loaded.value = true;
    } catch {
      toast.showErrorToast({
        title: 'Erro ao carregar dados do formulário',
        message: 'Não foi possível carregar colaboradores e cargos. Tente novamente.',
        type: 'error',
      });
    } finally {
      loading.value = false;
    }
  }

  return {
    members,
    loading,
    loaded,
    collaborators,
    unlistedMemberIds,
    visibleMemberIds,
    load,
    initializeParticipants,
  };
}
